import { useCallback, useMemo } from 'react'

import { getAccountId, getUserId } from '~/helpers'

type UseColumnVisibilityStorageOptions = {
  tableName: ColumnSelectorTables
}

type StoredSelectionByLabel = Record<string, boolean>

const STORAGE_KEY_PREFIX = 'vtexads:rm:table-columns'
const STORAGE_VERSION = 'v3'

const isStoredSelectionByLabel = (
  value: unknown
): value is StoredSelectionByLabel =>
  typeof value === 'object' &&
  value !== null &&
  Object.values(value).every(item => typeof item === 'boolean')

const buildStorageKey = (
  tableName: ColumnSelectorTables,
  accountId?: string,
  userId?: string
) => {
  if (!accountId || !userId) {
    return null
  }

  return `${STORAGE_KEY_PREFIX}:${STORAGE_VERSION}:${tableName}:${accountId}:${userId}`
}

const readStoredSelectionByLabel = (storageKey: string | null) => {
  if (!storageKey) {
    return null
  }

  const storedValue = localStorage.getItem(storageKey)

  if (!storedValue) {
    return null
  }

  try {
    const parsedValue = JSON.parse(storedValue) as unknown

    return isStoredSelectionByLabel(parsedValue) ? parsedValue : null
  } catch {
    return null
  }
}

export const useColumnVisibilityStorage = ({
  tableName
}: UseColumnVisibilityStorageOptions) => {
  const accountId = useMemo(() => getAccountId(), [])
  const userId = useMemo(() => getUserId(), [])

  const storageKey = useMemo(
    () => buildStorageKey(tableName, accountId, userId),
    [accountId, tableName, userId]
  )

  const storedSelectionByLabel = useMemo(
    () => readStoredSelectionByLabel(storageKey),
    [storageKey]
  )

  const persistSelectedLabels = useCallback(
    (columns: ColumnSelectorColumn[]) => {
      if (!storageKey) {
        return
      }

      const nextSelectionByLabel = columns.reduce<StoredSelectionByLabel>(
        (acc, column) => {
          acc[column.label] = column.isSelected
          return acc
        },
        { ...(storedSelectionByLabel ?? {}) }
      )

      localStorage.setItem(storageKey, JSON.stringify(nextSelectionByLabel))
    },
    [storageKey, storedSelectionByLabel]
  )

  return {
    storedSelectionByLabel,
    persistSelectedLabels
  }
}

export default useColumnVisibilityStorage
